import {
    InformationCircleIcon,
    MagnifyingGlassMinusIcon,
    MagnifyingGlassPlusIcon,
    PencilSquareIcon,
    SunIcon,
} from "@heroicons/react/24/solid";
import "./styles/charaterVisualiserUi.style.sass";
import IconButton from "../IconButton/IconButton";
import { useQuery } from "@tanstack/react-query";
import { api } from "@src/utils/axios-instance";
import { useEffect, useState } from "react";
import { Character, Version } from "@src/types/global";
import { useUserStore } from "@src/stores/userStore";
import { toImperialAsString } from "@src/utils/character-utils";

interface IProps {
    toggleZoom: (zoomedIn: boolean) => void;
    character: Character;
    selectedVersion: Version;
    zoomedIn: boolean;
    formControl: () => void;
    measurementUnit: "imperial" | "metric";
}

type Tabs = "info" | "presets" | "edit" | null;
const CharacterVisualiserUi = ({
    character,
    selectedVersion,
    toggleZoom,
    zoomedIn,
    formControl,
    measurementUnit = "metric",
}: IProps) => {
    const { id } = useUserStore();
    const [version, setVersion] = useState(selectedVersion);
    const [openedTab, setOpenedTab] = useState<Tabs>(null);
    async function getUserScenePresets() {
        const res = await api.get("/scene_presets");
        return res.data;
    }
    useEffect(() => {
        setVersion(selectedVersion);
    }, [selectedVersion]);
    const presetsQuery = useQuery({
        queryKey: ["scenePresets"],
        queryFn: getUserScenePresets,
    });

    function handleTabOpen(tab: Tabs) {
        if (tab == openedTab) {
            setOpenedTab(null);
            return;
        }
        setOpenedTab(tab);
        return;
    }
    const translatedHeight =
        measurementUnit == "metric"
            ? `${version.height} cm`
            : toImperialAsString(version.height);

    const translatedOverrideHeight =
        measurementUnit == "metric"
            ? `${version.override_height} cm`
            : toImperialAsString(version.override_height);
    return (
        <div className="visualiser__ui">
            <div className="visualiser__ui__buttons">
                <IconButton
                    ariaLabel="Toggle zoom"
                    onClick={() => toggleZoom(!zoomedIn)}
                    icon={
                        zoomedIn ? (
                            <MagnifyingGlassMinusIcon />
                        ) : (
                            <MagnifyingGlassPlusIcon />
                        )
                    }
                    opened={zoomedIn}
                />
                <span className="display-tab">
                    <IconButton
                        ariaLabel="Show character information"
                        onClick={() => handleTabOpen("info")}
                        icon={<InformationCircleIcon />}
                        opened={openedTab == "info"}
                    />
                </span>

                <span className="display-tab">
                    <IconButton
                        ariaLabel="Show scene presets"
                        onClick={() => handleTabOpen("presets")}
                        icon={<SunIcon />}
                        opened={openedTab == "presets"}
                    />
                </span>
                {character.owner_id == id && (
                    <IconButton
                        ariaLabel="Edit character information"
                        onClick={() => {
                            formControl();
                            handleTabOpen("edit");
                        }}
                        icon={<PencilSquareIcon />}
                        opened={openedTab == "edit"}
                    />
                )}
            </div>
            {openedTab && openedTab !== "edit" && (
                <span className="tab">
                    {openedTab == "info" && (
                        <div className="tab__info">
                            <span>
                                {character.is_public ? "Public" : "Private"}
                            </span>
                            <h2>{character.first_name}</h2>
                            <div className="details">
                                <strong>{version.pronouns}</strong>
                                <div>
                                    <strong>{translatedHeight}</strong>
                                    {version.override_height != null &&
                                    version.override_height != 0 ? (
                                        <p className="override_height">
                                            <em>{translatedOverrideHeight}</em>
                                        </p>
                                    ) : null}
                                </div>

                                <strong>
                                    {version.age}{" "}
                                    {Number.isInteger(parseInt(version.age))
                                        ? "years old"
                                        : null}
                                </strong>
                            </div>
                            <div className="about">
                                <h3>About:</h3>
                                {version.about}
                            </div>
                            <div className="traits">
                                <div className="traits__qualities">
                                    <h3>Qualities:</h3>
                                    <ul>
                                        {version.qualities &&
                                        !Array.isArray(version.qualities)
                                            ? version.qualities
                                                  .split(",")
                                                  .map(
                                                      (
                                                          t: string,
                                                          i: number,
                                                      ) => <li key={i}>{t}</li>,
                                                  )
                                            : //@ts-ignore
                                              version.qualities.map(
                                                  (t: string, i: number) => (
                                                      <li key={i}>{t}</li>
                                                  ),
                                              )}
                                    </ul>
                                </div>
                                <div className="traits__flaws">
                                    <h3>Flaws:</h3>
                                    <ul>
                                        {version.flaws &&
                                        !Array.isArray(version.flaws)
                                            ? version.flaws
                                                  //@ts-ignore
                                                  .split(",")
                                                  .map(
                                                      (
                                                          t: string,
                                                          i: number,
                                                      ) => <li key={i}>{t}</li>,
                                                  )
                                            : //@ts-ignore
                                              version.flaws.map(
                                                  (t: string, i: number) => (
                                                      <li key={i}>{t}</li>
                                                  ),
                                              )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                    {openedTab == "presets" && (
                        <div className="tab__presets">
                            <h3>Coming soon</h3>
                            {/* presetsQuery.isSuccess &&
                                presetsQuery.data.data.map((preset, i) => {
                                    <p key={i}>{preset.name}</p>;
			    }) */}
                        </div>
                    )}
                </span>
            )}
        </div>
    );
};
export default CharacterVisualiserUi;
